import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Upload, Camera, X, Send, Loader2, ChevronDown,
  CheckCircle, AlertTriangle, Clock, Eye, ArrowRight, Search, Filter
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_LABELS = {
  w2: 'W-2',
  tax_return: 'Tax Return',
  pay_stub: 'Pay Stub',
  bank_statement: 'Bank Statement',
};

const DOC_TYPE_OPTIONS = [
  { value: 'auto', label: 'Auto-Detect' },
  { value: 'w2', label: 'W-2 Form' },
  { value: 'tax_return', label: 'Tax Return (1040)' },
  { value: 'pay_stub', label: 'Pay Stub' },
  { value: 'bank_statement', label: 'Bank Statement' },
];

const CONFIDENCE_STYLES = {
  high: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  medium: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  low: 'bg-red-500/10 text-red-400 border-red-500/30',
};

function UploadSection({ token, onUploaded }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [docType, setDocType] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFile = useCallback((f) => {
    if (!f) return;
    setFile(f);
    setError(null);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  }, []);

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', docType);
      const res = await fetch(`${API}/api/ai/document-processing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        setFile(null);
        setPreview(null);
        setDocType('auto');
        onUploaded();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || `Error ${res.status}`);
      }
    } catch {
      setError('Upload failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <Card className="glass border-white/10 mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Upload className="w-5 h-5 text-emerald-400" />
          Upload New Document
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-4">
          <select data-testid="dh-doc-type" value={docType} onChange={e => setDocType(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none sm:w-48">
            {DOC_TYPE_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>

          <div
            data-testid="dh-upload-zone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            className={`flex-1 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
              dragOver ? 'border-emerald-500 bg-emerald-500/5' : file ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10 hover:border-white/20'
            }`}
          >
            <input ref={fileRef} type="file" className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*"
              capture="environment"
              onChange={e => handleFile(e.target.files?.[0])} />

            {file ? (
              <div className="flex items-center gap-3">
                {preview ? (
                  <img src={preview} alt="Preview" className="w-10 h-10 object-cover rounded-lg border border-white/10" />
                ) : (
                  <FileText className="w-10 h-10 text-gray-500" />
                )}
                <div className="text-left flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button data-testid="dh-clear-file" onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}
                  className="p-1.5 hover:bg-white/10 rounded-lg"><X className="w-4 h-4 text-gray-400" /></button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                <Upload className="w-4 h-4" />
                <span>Drop file here or click to browse</span>
                <Camera className="w-4 h-4 ml-2" />
              </div>
            )}
          </div>

          <Button data-testid="dh-upload-btn" onClick={upload} disabled={loading || !file}
            className="bg-emerald-600 hover:bg-emerald-700 sm:w-40 disabled:opacity-40">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Process'}
          </Button>
        </div>

        {error && (
          <div data-testid="dh-upload-error" className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DocumentCard({ doc, onSendToPrequalify }) {
  const [expanded, setExpanded] = useState(false);
  const label = DOC_TYPE_LABELS[doc.document_type] || doc.document_type;
  const confStyle = CONFIDENCE_STYLES[doc.confidence] || CONFIDENCE_STYLES.medium;
  const date = new Date(doc.processed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <Card data-testid={`doc-card-${doc.document_id}`} className="glass border-white/10">
      <CardContent className="p-4">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{label}</span>
                <Badge className={`text-[10px] px-1.5 py-0 border ${confStyle}`}>{doc.confidence}</Badge>
              </div>
              <p className="text-xs text-gray-500">{doc.document_id} &middot; {date}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {doc.prequalify_data && (
              <Button data-testid={`doc-send-pq-${doc.document_id}`} size="sm" variant="outline"
                onClick={() => onSendToPrequalify(doc)}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs h-8">
                <Send className="w-3 h-3 mr-1" /> Pre-Qualify
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)}
              className="text-gray-400 hover:text-white h-8 w-8 p-0">
              <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </div>

        {/* AI Summary */}
        {doc.ai_summary && (
          <p className="text-xs text-gray-400 leading-relaxed mb-3 line-clamp-2">{doc.ai_summary}</p>
        )}

        {/* Key metrics preview */}
        {!expanded && doc.extracted_data && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(doc.extracted_data).slice(0, 4).map(([k, v]) => (
              v !== null && (
                <span key={k} className="text-[10px] px-2 py-1 rounded-md bg-white/5 text-gray-400">
                  {k.replace(/_/g, ' ')}: <span className="text-white font-medium">{typeof v === 'number' ? v.toLocaleString() : String(v)}</span>
                </span>
              )
            ))}
            {Object.keys(doc.extracted_data).length > 4 && (
              <span className="text-[10px] px-2 py-1 text-gray-500">+{Object.keys(doc.extracted_data).length - 4} more</span>
            )}
          </div>
        )}

        {/* Expanded: Full extracted data */}
        <AnimatePresence>
          {expanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden">
              <div data-testid={`doc-data-${doc.document_id}`} className="mt-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                <p className="text-[10px] text-emerald-400 font-medium mb-2 uppercase tracking-wider">All Extracted Fields</p>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {Object.entries(doc.extracted_data).map(([k, v]) => (
                    <div key={k} className="flex justify-between items-baseline gap-2 py-1 px-2 rounded bg-white/[0.02]">
                      <span className="text-[11px] text-gray-500 capitalize">{k.replace(/_/g, ' ')}</span>
                      <span className={`text-[11px] font-medium ${v === null ? 'text-gray-600 italic' : 'text-white'}`}>
                        {v === null ? 'N/A' : typeof v === 'number' ? v.toLocaleString() : String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {doc.prequalify_data && (
                <div className="mt-2 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20">
                  <p className="text-[10px] text-blue-400 font-medium mb-1 uppercase tracking-wider">Pre-Qualification Data</p>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(doc.prequalify_data).map(([k, v]) => (
                      <div key={k}>
                        <span className="text-[10px] text-gray-500 capitalize">{k.replace(/_/g, ' ')}: </span>
                        <span className="text-xs font-medium text-white">${Math.round(v).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {doc.filename && (
                <p className="text-[10px] text-gray-600 mt-2">Original file: {doc.filename}</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

export default function DocumentHistory() {
  const navigate = useNavigate();
  const token = localStorage.getItem('kutro_token');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchDocs = async () => {
    try {
      const res = await fetch(`${API}/api/ai/documents`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch (e) {
      console.error('Failed to fetch documents:', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchDocs(); }, []);

  const handleSendToPrequalify = (doc) => {
    const params = new URLSearchParams();
    if (doc.prequalify_data?.annual_income) params.set('income', Math.round(doc.prequalify_data.annual_income));
    navigate(`/agents?tab=prequalification&${params.toString()}`);
  };

  const filtered = documents.filter(d => {
    if (filter !== 'all' && d.document_type !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return d.document_id.toLowerCase().includes(s) ||
        (d.filename || '').toLowerCase().includes(s) ||
        (d.ai_summary || '').toLowerCase().includes(s);
    }
    return true;
  });

  const typeCounts = documents.reduce((acc, d) => { acc[d.document_type] = (acc[d.document_type] || 0) + 1; return acc; }, {});

  return (
    <DashboardLayout title="Document History">
      {/* Upload Section */}
      <UploadSection token={token} onUploaded={() => { setLoading(true); fetchDocs(); }} />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Documents', value: documents.length, icon: FileText, color: 'text-violet-400' },
          { label: 'W-2 Forms', value: typeCounts.w2 || 0, icon: FileText, color: 'text-blue-400' },
          { label: 'Tax Returns', value: typeCounts.tax_return || 0, icon: FileText, color: 'text-emerald-400' },
          { label: 'PQ-Ready', value: documents.filter(d => d.prequalify_data).length, icon: Send, color: 'text-amber-400' },
        ].map((s, i) => (
          <Card key={i} className="glass border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <p data-testid={`dh-stat-${i}`} className="text-lg font-bold">{s.value}</p>
                <p className="text-[10px] text-gray-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter & Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {['all', 'w2', 'tax_return', 'pay_stub', 'bank_statement'].map(t => (
            <button key={t} data-testid={`dh-filter-${t}`} onClick={() => setFilter(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                filter === t ? 'bg-emerald-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}>
              {t === 'all' ? `All (${documents.length})` : `${DOC_TYPE_LABELS[t] || t} (${typeCounts[t] || 0})`}
            </button>
          ))}
        </div>
        <div className="relative flex-1 sm:max-w-xs ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input data-testid="dh-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search documents..."
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-gray-600 focus:border-emerald-500 focus:outline-none" />
        </div>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="glass border-white/10">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">
              {documents.length === 0 ? 'No Documents Yet' : 'No Matching Documents'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {documents.length === 0
                ? 'Upload a W-2, tax return, pay stub, or bank statement to get started.'
                : 'Try adjusting your filter or search term.'}
            </p>
            {documents.length === 0 && (
              <a href="/agents?tab=documents" data-testid="dh-go-agents"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-sm font-medium transition-colors text-white">
                Go to AI Agents <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </CardContent>
        </Card>
      ) : (
        <div data-testid="dh-documents-list" className="space-y-3">
          {filtered.map(doc => (
            <DocumentCard key={doc.document_id} doc={doc} onSendToPrequalify={handleSendToPrequalify} />
          ))}
        </div>
      )}

      {/* Copyright */}
      <p className="text-center text-xs text-gray-600 mt-8">&copy; 2026 Kojiru, Inc. All Rights Reserved.</p>
    </DashboardLayout>
  );
}
